'use client'

import { FormProvider } from '@ariakit/react'
import { ComponentProps } from 'react'
import styled from 'styled-components'

import {
  IconButton,
  QuickBuy as QuickBuyIcon,
  ResponsiveImage,
  Skeleton,
  Spacer,
  TypeStyleHeadlineExtraSmall,
  fromMd,
  untilMd,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import { trackEvent } from '../../../lib/analytics'
import { useNextQuery } from '../../_hooks/useNextQuery'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import {
  VariantSelectionColorRadioGroup,
  VariantSelectionFitRadioGroup,
  VariantSelectionForm,
  VariantSelectionFormFields,
  VariantSelectionFormPurchasingSubmit,
  VariantSelectionSizeRadioGroup,
  useVariantSelectionForm,
} from '../forms/VariantSelectionForm'
import { NextLink } from '../navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariant,
  derivePricingForVariantGroup,
} from '../pricing/PricingRange.client'
import { ProductTileDetails, ProductTilePrice, ProductTileTitle } from '../tiles/ProductTile.client'

import { NextDialogProvider, NextDialogTrigger } from './NextDialog.client'
import { NextDrawerDialog } from './NextDrawerDialog.client'

const quickBuyTrackingCategory = 'Quick Buy'

type QuickBuyDialogProps = QuickBuyDialogFormProps & {
  position: 'inline' | 'bottom-right'
} & ComponentProps<'button'>

export const QuickBuyDialog = styled(
  ({
    initialColor,
    initialFit,
    initialSize,
    collectionHandle,
    productGroupHandle,
    ...props
  }: QuickBuyDialogProps) => {
    const translations = useTranslation()
    return (
      <NextDialogProvider>
        <NextDialogTrigger
          render={
            <IconButton
              type='button'
              variant='black-on-white'
              size='small'
              aria-label={translations.quickBuy.modal.headline}
              {...trackEvent({
                action: 'click',
                category: `quick buy`,
                label: 'quick-buy',
              })}
              style={{ padding: '0px' }}
              {...props}
            >
              <QuickBuyIcon width='30px' height='30px' />
            </IconButton>
          }
        />
        <NextDrawerDialog
          unmountOnHide={true}
          variant='bottom'
          closeText={translations.magnolia.general.close}
        >
          <QuickBuyContent>
            <TitleBar>
              <TypeStyleHeadlineExtraSmall textTransform='uppercase'>
                {translations.quickBuy.modal.headline}
              </TypeStyleHeadlineExtraSmall>
            </TitleBar>
            <FormProvider>
              <QuickBuyDialogForm
                productGroupHandle={productGroupHandle}
                initialColor={initialColor}
                initialFit={initialFit}
                initialSize={initialSize}
                collectionHandle={collectionHandle}
              />
            </FormProvider>
          </QuickBuyContent>
        </NextDrawerDialog>
      </NextDialogProvider>
    )
  }
)`
  position: ${o => (o.position === 'inline' ? 'static' : 'absolute')};
  ${untilMd} {
    bottom: ${o => o.theme.spacing(2)};
    right: ${o => o.theme.spacing(2)};
  }
  ${fromMd} {
    bottom: ${o => o.theme.spacing(3)};
    right: ${o => o.theme.spacing(3)};
  }
`

type QuickBuyDialogFormProps = {
  productGroupHandle: string
  collectionHandle: string | null | undefined
  initialColor: string
  initialFit: string | null | undefined
  initialSize: string | null | undefined
}

const QuickBuyDialogForm: React.FC<QuickBuyDialogFormProps> = ({
  initialColor,
  initialFit,
  initialSize,
  productGroupHandle,
  collectionHandle,
}) => {
  const { formStore, queryVariables } = useVariantSelectionForm({
    defaultValues: {
      initialColor,
      initialFit,
      initialSize,
    },
  })

  const {
    data: currentData,
    previousData,
    status,
  } = useNextQuery(
    gql(`
      query GetQuickBuyProductInfo(
          $collectionHandle: String, 
          $catalogProductHandle: String!, 
          $colorFieldValue: String!, 
          $fitFieldValue: String!, 
          $sizeFieldValue: String!
      ) {
        productGroup(handle: $catalogProductHandle) {
          sizeChartCG {
            ...SizeChartFragment
          }
        }
        productCatalogStandardProduct(handle: $catalogProductHandle) {
          ...VariantSelectionFormProductFields
          variantGroup(color: $colorFieldValue) {
            ...VariantSelectionFormGroupFields
            images(first: 1, fit: $fitFieldValue) {
              url
            }
            priceRange {
              currencyCode
              min {
                amount
              }
              max {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              min {
                amount
              }
              max {
                amount
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        ...queryVariables,
        catalogProductHandle: productGroupHandle,
        collectionHandle,
      },
    }
  )

  const data = currentData ?? previousData

  if (!data) {
    if (status === 'idle' || status === 'pending') {
      return <QuickbuyFormSkeleton />
    } else {
      // TODO: Create and render a QuickBuyFormNotFound component
      return null
    }
  }

  if (status === 'rejected') {
    // TODO: Create and render a QuickBuyFormNotFound component
    return null
  }

  const product = data.productCatalogStandardProduct
  const variantGroup = data.productCatalogStandardProduct.variantGroup
  const selectedVariant = data.productCatalogStandardProduct.variantGroup.variant

  const pdpUrl = selectedVariant.urlPath
  const selectedImage = variantGroup.images?.[0]?.url
  const pricingRange = selectedVariant
    ? derivePricingForVariant(selectedVariant)
    : derivePricingForVariantGroup(variantGroup)

  return (
    <>
      <ProductPreview>
        {selectedImage && (
          <NextLink
            href={pdpUrl}
            {...trackEvent({
              action: 'click',
              category: quickBuyTrackingCategory,
              label: 'Click Product',
              value: productGroupHandle,
            })}
          >
            <ProductImage
              srcs={{
                sm: selectedImage,
                md: selectedImage,
              }}
              src={selectedImage}
              widths={{ unit: 'px', sm: 100, md: 100 }}
              alt={variantGroup.title}
              loading='default'
              aspectRatios={{ sm: 1, md: 1 }}
            />
          </NextLink>
        )}
        <ProductTileDetails untilMdLeftPadding={false} noMargin>
          <NextLink
            href={pdpUrl}
            {...trackEvent({
              action: 'click',
              category: quickBuyTrackingCategory,
              label: 'Click Product',
              value: productGroupHandle,
            })}
          >
            <ProductTileTitle>{variantGroup.title}</ProductTileTitle>
          </NextLink>
          {pricingRange ? (
            <ProductTilePrice>
              <PricingRange upToPercentageOff={null} percentageOff={null} {...pricingRange} />
            </ProductTilePrice>
          ) : null}
        </ProductTileDetails>
      </ProductPreview>
      <VariantSelectionForm
        store={formStore}
        product={product}
        selectedVariant={selectedVariant}
        variantGroup={variantGroup}
        trackingCategory={quickBuyTrackingCategory}
        loading={status === 'pending'}
      >
        <VariantSelectionFormFields>
          <VariantSelectionColorRadioGroup />
          <VariantSelectionSizeRadioGroup sizeChart={data.productGroup?.sizeChartCG} />
          <VariantSelectionFitRadioGroup />
        </VariantSelectionFormFields>

        <QuickBuyDialogFooter>
          <VariantSelectionFormPurchasingSubmit isPrimaryCtaForPageView={false} />
        </QuickBuyDialogFooter>
      </VariantSelectionForm>
    </>
  )
}

const QuickbuyFormSkeleton: React.FC = () => (
  <div>
    <ProductPreview>
      <Skeleton height={'80px'} width={'80px'}></Skeleton>
      <ProductTileDetails untilMdLeftPadding={false} noMargin>
        <Skeleton height={'20px'} width={'100px'}></Skeleton>
        <Skeleton height={'20px'} width={'100px'}></Skeleton>
      </ProductTileDetails>
    </ProductPreview>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'30px'} width={'150px'}></Skeleton>
    <Spacer height={8} width={1}></Spacer>
    <Skeleton height={'30px'} width={'300px'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
    <Skeleton height={'50px'} width={'100%'}></Skeleton>
    <Spacer height={24} width={1}></Spacer>
  </div>
)

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${o => o.theme.spacing(4)};
`

const ProductPreview = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${o => o.theme.spacing(4)};
  gap: ${o => o.theme.spacing(2)};
`

const QuickBuyContent = styled.div`
  text-align: left;
  padding: ${o => o.theme.spacing(6, 4, 0, 4)};
  max-width: 812px;
  ${fromMd} {
    min-width: 812px;
  }
  margin-left: auto;
  margin-right: auto;
`

const ProductImage = styled(ResponsiveImage)`
  width: 80px;
`

const QuickBuyDialogFooter = styled.div`
  padding-top: ${o => o.theme.spacing(6)};
  padding-bottom: ${o => o.theme.spacing(6)};
  width: 100%;
  display: flex;
  align-items: stretch;
  flex-direction: column;
`
